import { clamp } from "@/utils/math"
import { usePointerDrag } from "@/utils/usePointerDrag"
import clsx from "clsx"
import { useState } from "react"
import { getDefaultRelativeShiftRange } from "./utils"
import { isDeepEqual } from "@/utils/object"

export const HardSoftRangeSlider = (props: {
	disabled?: boolean
	background: string
	initial: number
	range: [number, number, number, number]
	onUpdate: (value) => void
	onUpdateEnded: (displayValue) => void
	onSliderDown?: () => void
	onSliderUp?: () => void
}) => {
	const [hardStart, softStart, softEnd, hardEnd] = props.range

	const [isHovering, setHovering] = useState(false)
	const [startRange, setStartRange] = useState(props.range)

	/* Actions */

	const bindSoftEdgeSliderDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			e.stopPropagation()
			e.preventDefault()

			setStartRange([...props.range])
			props.onSliderDown?.()
		},
		onPointerUp() {
			handleCommitUpdate()
			props.onSliderUp?.()
		},
		onDrag(drag) {
			const name = drag.currentTarget.getAttribute("data-handle")
			const rect = drag.currentTarget.parentElement?.parentElement?.getBoundingClientRect()

			const x = drag.x - (rect?.left ?? 0)
			const width = rect?.width ?? 1

			const range = props.range

			if (name === "start") {
				const value = clamp(x / width, 0, props.initial - 0.05)

				range[1] = value
				range[0] = Math.min(value, range[0])
			} else {
				const value = clamp(x / width, props.initial + 0.05, 1)

				range[2] = value
				range[3] = Math.max(value, range[3])
			}

			props.onUpdate(range)
		},
	})

	const bindInternalSliderDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			e.stopPropagation()
			e.preventDefault()

			setStartRange([...props.range])
			props.onSliderDown?.()
		},
		onPointerUp() {
			handleCommitUpdate()
			props.onSliderUp?.()
		},
		onDrag(drag) {
			if (drag.isStart) {
				return
			}

			const rect = drag.currentTarget.parentElement?.getBoundingClientRect()
			const width = rect?.width ?? 1
			const deltaX = drag.deltaX

			const deltaValue = (deltaX / width) * 0.6

			const range = [...startRange]

			let softStart = clamp(startRange[1] + deltaValue, 0, props.initial - 0.05)
			let softEnd = clamp(startRange[2] + deltaValue, props.initial + 0.05, 1)

			// Calculate initial width of the internal slider
			const initialWidth = startRange[2] - startRange[1]
			const currentWidth = softEnd - softStart

			if (currentWidth > initialWidth) {
				if (drag.event.movementX > 0 && softEnd <= props.initial + 0.05) {
					softEnd = props.initial + 0.05
				}

				if (drag.event.movementX < 0 && softStart >= props.initial + 0.05) {
					softStart = props.initial - 0.05
				}

				range[1] = softStart
				range[2] = softEnd
			} else {
				range[1] = softStart
				range[2] = softEnd
			}

			props.onUpdate(range)
		},
	})

	const bindHardHandleDrag = usePointerDrag<HTMLDivElement>({
		onPointerDown(e) {
			e.stopPropagation()
			e.preventDefault()

			setStartRange([...props.range])
			props.onSliderDown?.()
		},
		onPointerUp() {
			handleCommitUpdate()
			props.onSliderUp?.()
		},
		onDrag(drag) {
			const rect = drag.currentTarget.parentElement?.getBoundingClientRect()

			const x = drag.x - (rect?.left ?? 0)
			const width = rect?.width ?? 1

			const name = x / width < props.initial - 0.05 ? "start" : "end"

			const range = props.range

			if (name === "start") {
				const value = clamp(x / width, 0, props.initial - 0.05)

				range[0] = value
				range[1] = Math.max(value, range[1])
			} else {
				const value = clamp(x / width, props.initial + 0.05, 1)

				range[3] = value
				range[2] = Math.min(value, range[2])
			}

			props.onUpdate(range)
		},
	})

	const handleCommitUpdate = () => {
		const range = props.range

		// @din: is there a better way? == and === didn't really work
		if (!isDeepEqual(range, startRange)) {
			props.onUpdateEnded(formatRangeDisplayValue(range))
		}
	}

	const handlePointerEnter = () => {
		if (props.disabled === true) {
			return
		}

		setHovering(true)
	}

	const handlePointerLeave = () => {
		if (props.disabled === true) {
			return
		}

		setHovering(false)
	}

	const handleReset = () => {
		if (props.disabled === true) {
			return
		}

		const range = getDefaultRelativeShiftRange(props.initial)

		props.onUpdate(range)
		props.onUpdateEnded(formatRangeDisplayValue(range))
	}

	/* Rendering */

	const formatRangeDisplayValue = (range: [number, number, number, number]) => {
		const value = `${Math.round(range[0] * 100)}/${Math.round(range[1] * 100)} - ${Math.round(
			range[2] * 100,
		)}/${Math.round(range[3] * 100)}`
		return value
	}

	const renderEdge = (name: string, left: number) => {
		let style

		if (name === "start") {
			style = { left: `${left * 100}%` }
		} else {
			style = { left: `${left * 100}%` }
		}

		return (
			<div
				style={style}
				data-handle={name}
				className={clsx(
					"absolute inset-y-0 w-[2px]",
					isHovering === true
						? "bg-black/85 dark:bg-white/90"
						: "bg-black/60 dark:bg-white/65",
				)}
			>
				<span
					className={clsx(
						"absolute -bottom-[8px] size-2 -translate-x-[3px] border-x-4 border-b-8 border-x-transparent",
						isHovering === true
							? "border-b-black/85 dark:border-b-white/90"
							: "border-b-black/60 dark:border-b-white/65",
					)}
				></span>
			</div>
		)
	}

	const renderInteger = (value: number) => {
		return `${Math.round(value * 100)}`
	}

	const renderMiddlePoint = (value: number) => {
		return (
			<div
				style={{ left: `${value * 100}%` }}
				className={clsx(
					"pointer-events-none absolute top-[50%] size-2 -translate-x-1/2 -translate-y-[50%] rounded-full border shadow-sm",
					"border-white/85 bg-black/70 dark:border-black/80 dark:bg-white/65",
				)}
			></div>
		)
	}

	const renderInternalSliderEdge = (name: string) => {
		if (isHovering === false) {
			return null
		}

		return (
			<div
				data-handle={name}
				{...bindSoftEdgeSliderDrag}
				className={clsx(
					"absolute inset-y-1 z-[100] w-[7px] border-[3px]",
					name === "start" ? "-left-[8px]" : "-right-[8px]",
					isHovering === true ? "border-black/90 dark:border-white/95" : "border-white/0",
				)}
			></div>
		)
	}

	const renderExternalSlider = () => {
		return (
			<div className="absolute -inset-x-[10px] -inset-y-[14px]" {...bindHardHandleDrag}></div>
		)
	}

	const renderInternalSlider = () => {
		return (
			<div
				style={{ left: `${softStart * 100}%`, right: `${(1.0 - softEnd) * 100}%` }}
				className={clsx(
					"absolute -inset-y-[3px] rounded border-2",
					isHovering === true
						? "border-base-500 dark:border-base-100/95"
						: "border-base-400 dark:border-base-200/65",
				)}
			>
				<div {...bindInternalSliderDrag} className="absolute inset-0"></div>
				{renderInternalSliderEdge("start")}
				{renderInternalSliderEdge("end")}
			</div>
		)
	}

	return (
		<div
			className="flex flex-row items-center justify-between pb-3 pt-2 text-xs"
			onPointerLeave={handlePointerLeave}
			onPointerEnter={handlePointerEnter}
			onDoubleClick={handleReset}
		>
			{/* Left Side Of Range */}
			<div
				className={clsx(
					"w-[65px] overflow-hidden transition-opacity",
					isHovering === true ? "text-base-50" : "text-base-400 opacity-50",
				)}
			>
				{renderInteger(hardStart)}/{renderInteger(softStart)}
			</div>
			{/* External Slider */}
			<div
				style={{ background: props.background }}
				className="relative h-6 w-full border border-white/35 dark:border-white/35"
			>
				{/* Edges */}
				{renderEdge("start", hardStart)}
				{renderEdge("end", hardEnd)}
				{/* External Slider */}
				{renderExternalSlider()}
				{/* Internal Slider */}
				{renderInternalSlider()}
				{/* Initial (middle) point */}
				{renderMiddlePoint(props.initial)}
			</div>
			{/* Right Side Of Range */}
			<div
				className={clsx(
					"w-[80px] overflow-hidden text-right transition-opacity",
					isHovering === true ? "text-base-50" : "text-base-400 opacity-50",
				)}
			>
				{renderInteger(softEnd)}/{renderInteger(hardEnd)}
			</div>
		</div>
	)
}
